<app-header></app-header>
<app-leftsidebar></app-leftsidebar>
<main class="onboarding-shell">
<div class="onboarding-page">
  <div class="ob-head">
    <span class="ob-eyebrow">Become a seller</span>
    <h1>Set up your shop</h1>
    <p class="lede">Three quick steps, then you can sell maps, modules and campaigns to the WorldTumbler community.</p>
  </div>

  @if (loading) {
    <div class="ob-wrap" aria-busy="true" aria-label="Loading">
      <span class="ob-skel ob-skel--steps"></span>
      <div class="ob-card">
        <span class="ob-skel ob-skel--title"></span>
        <span class="ob-skel ob-skel--line"></span>
        <span class="ob-skel ob-skel--field"></span>
        <span class="ob-skel ob-skel--field ob-skel--tall"></span>
        <span class="ob-skel ob-skel--btn"></span>
      </div>
    </div>
  } @else if (loadError) {
    <div class="ob-empty">
      <span class="ob-empty-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><line x1="12" y1="7.5" x2="12" y2="12.5" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>
      </span>
      @if (loadError === 'signin') {
        <h3>Please sign in again</h3>
        <p>Your sign-in has expired, so we couldn't open your shop setup.</p>
        <a routerLink="/login" class="wt-btn-primary">Sign in</a>
      } @else {
        <h3>Couldn't open shop setup</h3>
        <p>Check your connection and try again.</p>
        <button type="button" class="wt-btn-primary" (click)="load()">Try again</button>
      }
    </div>
  } @else {
    <div class="ob-wrap">
      <!-- ===== Step tracker ===== -->
      <ol class="ob-steps">
        @for (s of steps; track s.key; let i = $index) {
          <li
            class="ob-step"
            [class.is-done]="i < currentIndex"
            [class.is-current]="i === currentIndex"
            [attr.aria-current]="i === currentIndex ? 'step' : null"
          >
            @if (i < currentIndex && step !== 'done') {
              <button type="button" class="ob-step-btn" (click)="goTo(s.key)" [attr.aria-label]="'Back to ' + s.label">
                <span class="ob-step-dot">
                  <svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
                </span>
                <span class="ob-step-label">{{ s.label }}</span>
              </button>
            } @else {
              <span class="ob-step-dot">
                @if (i < currentIndex) {
                  <svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
                } @else {
                  {{ i + 1 }}
                }
              </span>
              <span class="ob-step-label">{{ s.label }}</span>
            }
          </li>
        }
      </ol>

      <!-- ===== 1. Shop ===== -->
      @if (step === 'branding') {
        <div class="ob-card">
          <h2>Name your shop</h2>
          <p class="ob-copy">This is what buyers see on your shop page and next to your items. You can change it any time.</p>

          <div class="ob-branding">
            <div class="ob-fields">
              <label class="ob-field">
                <span class="ob-field-label">Shop name <span class="required-star">*</span><em>{{ shopName.length }}/150</em></span>
                <input type="text" maxlength="150" [(ngModel)]="shopName" placeholder="e.g. Daybreak Collective" />
                <small class="ob-field-hint">Shown on your shop page and next to every item you sell.</small>
              </label>
              <label class="ob-field">
                <span class="ob-field-label">About your shop <em>{{ bio.length }}/1000</em></span>
                <textarea rows="5" maxlength="1000" [(ngModel)]="bio" placeholder="What do you make? Which systems and settings?"></textarea>
                <small class="ob-field-hint">Optional. A sentence or two helps buyers find you.</small>
              </label>
            </div>

            <!-- Live preview: the same card buyers see on All Shops -->
            <div class="ob-preview" aria-label="Preview">
              <span class="ob-preview-label">How buyers will see it</span>
              <div class="ob-preview-card">
                <span class="ob-preview-banner"></span>
                <span class="ob-preview-logo ob-preview-logo--letter">{{ (shopName || '?').trim().charAt(0).toUpperCase() || '?' }}</span>
                <div class="ob-preview-body">
                  <strong [class.is-placeholder]="!shopName.trim()">{{ shopName.trim() || 'Your shop name' }}</strong>
                  <span class="ob-preview-stats">0 items · 0 followers</span>
                  <span class="ob-preview-bio" [class.is-placeholder]="!bio.trim()">{{ bio.trim() || 'A few words about what you make.' }}</span>
                </div>
                <div class="ob-preview-foot">
                  <span class="ob-preview-new">New shop</span>
                  <span class="ob-preview-visit">Visit shop
                    <svg viewBox="0 0 24 24" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
                  </span>
                </div>
              </div>
              <span class="ob-preview-hint">Add a logo and banner later in My shop → Profile.</span>
            </div>
          </div>

          <div class="ob-actions">
            <button type="button" class="wt-btn-primary ob-next" [disabled]="saving || !shopName.trim()" (click)="saveBranding()">
              {{ saving ? 'Saving…' : 'Continue' }}
              <svg viewBox="0 0 24 24" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
            </button>
          </div>
        </div>
      }

      <!-- ===== 2. Agreement ===== -->
      @if (step === 'agreement') {
        <div class="ob-card">
          <h2>Seller agreement</h2>
          <p class="ob-copy">The short version of what selling on WorldTumbler means:</p>
          <ul class="ob-terms">
            <li>
              <span class="ob-term-icon" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /></svg></span>
              <span><strong>You own what you sell.</strong> You have the rights to everything you list.</span>
            </li>
            <li>
              <span class="ob-term-icon" aria-hidden="true"><svg viewBox="0 0 24 24"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg></span>
              <span><strong>WorldTumbler keeps 20%</strong> of each sale; you get the rest, paid out through Stripe.</span>
            </li>
            <li>
              <span class="ob-term-icon" aria-hidden="true"><svg viewBox="0 0 24 24"><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg></span>
              <span><strong>New shops have part of each sale held</strong> for a short time before it's paid out.</span>
            </li>
            <li>
              <span class="ob-term-icon" aria-hidden="true"><svg viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" /><polyline points="14 2 14 8 20 8" /></svg></span>
              <span><strong>You handle your own taxes</strong> on what you earn.</span>
            </li>
          </ul>
          @if (agreementAccepted) {
            <p class="ob-accepted">
              <svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
              You accepted the Seller Agreement on {{ vendor?.terms_accepted_at | date: 'd MMM y' }}.
            </p>
          } @else {
            <label class="ob-check">
              <input type="checkbox" [(ngModel)]="agreementChecked" />
              <span>I have read and accept the Seller Agreement</span>
            </label>
          }
          <div class="ob-actions ob-actions--split">
            <button type="button" class="ob-back" (click)="back()" [disabled]="saving">
              <svg viewBox="0 0 24 24" aria-hidden="true"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
              Back
            </button>
            @if (agreementAccepted) {
              <button type="button" class="wt-btn-primary ob-next" (click)="continueFromAgreement()">
                Continue
                <svg viewBox="0 0 24 24" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
              </button>
            } @else {
              <button type="button" class="wt-btn-primary ob-next" [disabled]="!agreementChecked || saving" (click)="acceptAgreement()">
                {{ saving ? 'Saving…' : 'Accept & continue' }}
                <svg viewBox="0 0 24 24" aria-hidden="true"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>
              </button>
            }
          </div>
        </div>
      }

      <!-- ===== 3. Payouts ===== -->
      @if (step === 'stripe') {
        <div class="ob-card">
          <h2>Get paid</h2>
          @if (connectStatus?.details_submitted) {
            <div class="ob-note ob-note--wait">
              <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" /></svg>
              <span><strong>Stripe is reviewing your details.</strong> This usually takes a few minutes. You can sell free items in the meantime.</span>
            </div>
          } @else {
            <p class="ob-copy">
              WorldTumbler pays sellers through Stripe. You'll go to Stripe to confirm who you are and add a bank account,
              then come straight back here.
            </p>
          }
          <ul class="ob-checklist">
            <li><svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg> About 5 minutes</li>
            <li><svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg> Have your ID and bank details ready</li>
            <li><svg viewBox="0 0 24 24" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg> Needed only for paid items</li>
          </ul>
          <div class="ob-actions ob-actions--split">
            <button type="button" class="ob-back" (click)="back()" [disabled]="saving">
              <svg viewBox="0 0 24 24" aria-hidden="true"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
              Back
            </button>
            <button type="button" class="ob-skip" (click)="skipPayouts()">Skip for now — I'll only share free items</button>
            <button type="button" class="wt-btn-primary ob-next" [disabled]="saving" (click)="startStripeOnboarding()">
              {{ saving ? 'Opening Stripe…' : (connectStatus?.details_submitted ? 'Check with Stripe' : 'Continue to Stripe') }}
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>
            </button>
          </div>
        </div>
      }

      <!-- ===== Done ===== -->
      @if (step === 'done') {
        <div class="ob-card ob-card--done">
          <span class="ob-done-tick" aria-hidden="true"><svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg></span>
          <h2>Your shop is ready!</h2>
          <p class="ob-copy">Create an item in your Content Vault and use <strong>Sell in store</strong> on its page to list it.</p>
          <div class="ob-actions ob-actions--center">
            <a routerLink="/marketplace/vendor-dashboard" class="wt-btn-primary ob-next">Go to My shop</a>
            <a routerLink="/add-edit-content" class="ob-skip">Create your first item</a>
          </div>
        </div>
      }
    </div>
  }
</div>
</main>
<app-footer></app-footer>
